import React, { useState, useEffect } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import { useAuth } from '../context/AuthContext.tsx'
import api from '../services/api.ts'

interface InvitationDetails {
  email: string
  name: string
  role: string
  expires_at: string
  company: { id: string; name: string } | null
}

const AcceptInvite: React.FC = () => {
  const { token } = useParams<{ token: string }>()
  const navigate = useNavigate()
  const { checkAuth } = useAuth()
  const [invitation, setInvitation] = useState<InvitationDetails | null>(null)
  const [loadingInvite, setLoadingInvite] = useState(true)
  const [inviteError, setInviteError] = useState<string | null>(null)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    const fetchInvitation = async () => {
      try {
        const res = await api.get(`/api/invitations/${token}`)
        setInvitation(res.data.invitation)
      } catch (err: any) {
        setInviteError(err.response?.data?.message || 'Invalid or expired invitation')
      } finally {
        setLoadingInvite(false)
      }
    }
    if (token) fetchInvitation()
  }, [token])

  // Parse invitation name into first/last (best effort)
  const parseInviteName = (name: string) => {
    const parts = name.trim().split(/\s+/)
    if (parts.length >= 2) {
      return { firstName: parts[0], lastName: parts.slice(1).join(' ') }
    }
    return { firstName: name, lastName: '' }
  }

  const initialName = invitation
    ? parseInviteName(invitation.name)
    : { firstName: '', lastName: '' }

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      firstName: initialName.firstName,
      lastName: initialName.lastName,
      password: '',
      confirmPassword: '',
    },
    validationSchema: Yup.object({
      firstName: Yup.string()
        .min(1, 'First name is required')
        .max(50, 'First name must be 50 characters or less')
        .matches(
          /^[a-zA-Z\s'-]+$/,
          'First name can only contain letters, spaces, hyphens, and apostrophes',
        )
        .required('First name is required'),
      lastName: Yup.string()
        .min(1, 'Last name is required')
        .max(50, 'Last name must be 50 characters or less')
        .matches(
          /^[a-zA-Z\s'-]+$/,
          'Last name can only contain letters, spaces, hyphens, and apostrophes',
        )
        .required('Last name is required'),
      password: Yup.string()
        .min(8, 'Password must be at least 8 characters')
        .matches(
          /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
          'Password must contain at least one uppercase letter, one lowercase letter, and one number',
        )
        .required('Password is required'),
      confirmPassword: Yup.string()
        .oneOf([Yup.ref('password')], 'Passwords must match')
        .required('Please confirm your password'),
    }),
    onSubmit: async values => {
      setSubmitError(null)
      setSubmitting(true)
      try {
        const res = await api.post(`/api/invitations/${token}/accept`, {
          firstName: values.firstName,
          lastName: values.lastName,
          password: values.password,
        })
        await checkAuth()
        navigate(res.data.redirect || '/companies')
      } catch (err: any) {
        setSubmitError(
          err.response?.data?.message || 'Failed to accept invitation. Please try again.',
        )
      } finally {
        setSubmitting(false)
      }
    },
  })

  if (loadingInvite) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading invitation...</p>
        </div>
      </div>
    )
  }

  if (inviteError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-red-50 to-orange-100 py-12 px-4">
        <div className="max-w-md w-full text-center">
          <div className="bg-white py-8 px-6 shadow-xl rounded-lg border border-gray-200">
            <svg
              className="mx-auto h-16 w-16 text-red-400 mb-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"
              />
            </svg>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Invitation Unavailable</h2>
            <p className="text-gray-600 mb-6">{inviteError}</p>
            <div className="space-y-3">
              <Link
                to="/login"
                className="block w-full py-3 px-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-center font-medium"
              >
                Sign in to your account
              </Link>
              <Link
                to="/signup"
                className="block w-full py-3 px-4 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-center font-medium"
              >
                Create a new account
              </Link>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (!invitation) return null

  const inputClass = (field: keyof typeof formik.values) =>
    `appearance-none relative block w-full px-3 py-3 border ${
      formik.touched[field] && formik.errors[field]
        ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
        : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500'
    } placeholder-gray-500 text-gray-900 rounded-lg focus:outline-none focus:ring-2 focus:ring-opacity-50 transition-colors duration-200`

  const fieldError = (field: keyof typeof formik.values) =>
    formik.touched[field] && formik.errors[field] ? (
      <p className="mt-2 text-sm text-red-600">{formik.errors[field]}</p>
    ) : null

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div className="text-center">
            <h2 className="mt-6 text-3xl font-extrabold text-gray-900">Accept Your Invitation</h2>
            <p className="mt-2 text-sm text-gray-600">
              You've been invited to join{' '}
              <span className="font-semibold">{invitation.company?.name}</span>
            </p>
          </div>

          <div className="bg-white py-8 px-6 shadow-xl rounded-lg border border-gray-200">
            {/* Invitation info banner */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-blue-800">
                <span className="font-medium">Role:</span>{' '}
                {invitation.role === 'ADMIN' ? 'Company Admin' : 'Member'}
              </p>
              <p className="text-sm text-blue-700 mt-1">
                Set up your password below to get started.
              </p>
            </div>

            <form onSubmit={formik.handleSubmit} className="space-y-6">
              {/* Email (read-only) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email address
                </label>
                <input
                  type="email"
                  value={invitation.email}
                  disabled
                  className="appearance-none block w-full px-3 py-3 border border-gray-200 bg-gray-50 text-gray-500 rounded-lg cursor-not-allowed"
                />
              </div>

              {/* Name fields */}
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                <div>
                  <label
                    htmlFor="firstName"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    First name
                  </label>
                  <input
                    id="firstName"
                    name="firstName"
                    type="text"
                    autoComplete="given-name"
                    className={inputClass('firstName')}
                    placeholder="First name"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.firstName}
                    disabled={submitting}
                  />
                  {fieldError('firstName')}
                </div>
                <div>
                  <label
                    htmlFor="lastName"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Last name
                  </label>
                  <input
                    id="lastName"
                    name="lastName"
                    type="text"
                    autoComplete="family-name"
                    className={inputClass('lastName')}
                    placeholder="Last name"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.lastName}
                    disabled={submitting}
                  />
                  {fieldError('lastName')}
                </div>
              </div>

              {/* Password */}
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                  Password
                </label>
                <input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="new-password"
                  className={inputClass('password')}
                  placeholder="Create a strong password"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.password}
                  disabled={submitting}
                />
                {fieldError('password')}
              </div>

              {/* Confirm Password */}
              <div>
                <label
                  htmlFor="confirmPassword"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Confirm Password
                </label>
                <input
                  id="confirmPassword"
                  name="confirmPassword"
                  type="password"
                  autoComplete="new-password"
                  className={inputClass('confirmPassword')}
                  placeholder="Confirm your password"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.confirmPassword}
                  disabled={submitting}
                />
                {fieldError('confirmPassword')}
              </div>

              {submitError && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <p className="text-sm text-red-700">{submitError}</p>
                </div>
              )}

              <button
                type="submit"
                disabled={submitting || !formik.isValid}
                className={`w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-lg text-white ${
                  submitting || !formik.isValid
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200'
                }`}
              >
                {submitting ? (
                  <div className="flex items-center">
                    <svg
                      className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    Creating account...
                  </div>
                ) : (
                  'Accept Invitation & Create Account'
                )}
              </button>
            </form>

            <div className="mt-6 text-center">
              <p className="text-sm text-gray-500">
                Already have an account?{' '}
                <Link to="/login" className="font-medium text-blue-600 hover:text-blue-500">
                  Sign in
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default AcceptInvite
